import { base64Decode, base64Encode, concatBytes, utf8 } from "./encoding";
import { bigintToFieldHex, BN254_SCALAR_MODULUS, sha256 } from "./field";
import { walletDerivationMessage } from "./signingMessage";

export type V2KeyContext = {
  chainId: string;
  proxyAddress: string;
  walletAddress: string;
};

export type V2Keys = {
  rootSeed: Uint8Array;
  ownerKey: string;
  scanKey: Uint8Array;
  recoveryId: string;
};

export class NonDeterministicWalletSignatureError extends Error {
  constructor() {
    super(
      "This wallet did not return the same signature twice. Export an encrypted Redacted recovery package before depositing."
    );
  }
}

const contextBytes = (context: V2KeyContext): Uint8Array =>
  utf8(
    JSON.stringify([
      "redacted-v2",
      context.chainId,
      context.proxyAddress,
      context.walletAddress.toLowerCase(),
    ])
  );

const hkdf = async (
  input: Uint8Array,
  salt: Uint8Array,
  info: string
): Promise<Uint8Array> => {
  const key = await crypto.subtle.importKey("raw", input, "HKDF", false, [
    "deriveBits",
  ]);
  return new Uint8Array(
    await crypto.subtle.deriveBits(
      { name: "HKDF", hash: "SHA-256", salt, info: utf8(info) },
      key,
      256
    )
  );
};

export const deriveKeysFromSeed = async (
  rootSeed: Uint8Array,
  context: V2KeyContext
): Promise<V2Keys> => {
  if (rootSeed.length !== 32)
    throw new Error("Private Mode root seed must be 32 bytes");
  const salt = await sha256(contextBytes(context));
  const [ownerBytes, scanKey, recoveryBytes] = await Promise.all([
    hkdf(rootSeed, salt, "redacted-v2-owner-key"),
    hkdf(rootSeed, salt, "redacted-v2-note-scan-key"),
    hkdf(rootSeed, salt, "redacted-v2-recovery-id"),
  ]);
  const ownerKey = bigintToFieldHex(
    BigInt(
      `0x${[...ownerBytes].map((byte) => byte.toString(16).padStart(2, "0")).join("")}`
    ) % BN254_SCALAR_MODULUS
  );
  const recoveryId = base64Encode((await sha256(recoveryBytes)).slice(0, 16));
  return { rootSeed: new Uint8Array(rootSeed), ownerKey, scanKey, recoveryId };
};

export const deriveKeysFromWallet = async (params: {
  context: V2KeyContext;
  signDerivationMessage: (message: string) => Promise<string>;
}): Promise<V2Keys> => {
  const message = walletDerivationMessage(params.context);
  const first = await params.signDerivationMessage(message);
  const second = await params.signDerivationMessage(message);
  if (!first || first !== second)
    throw new NonDeterministicWalletSignatureError();
  const salt = await sha256(contextBytes(params.context));
  const rootSeed = await hkdf(
    utf8(first),
    salt,
    "redacted-v2-wallet-root-seed"
  );
  return deriveKeysFromSeed(rootSeed, params.context);
};

export const generateRandomKeys = async (
  context: V2KeyContext
): Promise<V2Keys> =>
  deriveKeysFromSeed(crypto.getRandomValues(new Uint8Array(32)), context);

export type EncryptedRecoveryPackage = {
  version: 2;
  walletAddress: string;
  kdf: "PBKDF2-SHA256";
  iterations: 600_000;
  salt: string;
  nonce: string;
  ciphertext: string;
  contextHash: string;
};

const recoveryContextHash = async (context: V2KeyContext): Promise<string> =>
  base64Encode(await sha256(contextBytes(context)));

const passphraseKey = async (
  passphrase: string,
  salt: Uint8Array,
  iterations: number
): Promise<CryptoKey> => {
  if (passphrase.length < 14) {
    throw new Error("Recovery passphrase must contain at least 14 characters");
  }
  const imported = await crypto.subtle.importKey(
    "raw",
    utf8(passphrase),
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", hash: "SHA-256", salt, iterations },
    imported,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
};

export const createRecoveryPackage = async (
  keys: V2Keys,
  context: V2KeyContext,
  passphrase: string
): Promise<EncryptedRecoveryPackage> => {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const contextHash = await recoveryContextHash(context);
  const key = await passphraseKey(passphrase, salt, 600_000);
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: nonce, additionalData: utf8(contextHash) },
      key,
      keys.rootSeed
    )
  );
  return {
    version: 2,
    walletAddress: context.walletAddress,
    kdf: "PBKDF2-SHA256",
    iterations: 600_000,
    salt: base64Encode(salt),
    nonce: base64Encode(nonce),
    ciphertext: base64Encode(ciphertext),
    contextHash,
  };
};

export const openRecoveryPackage = async (
  value: EncryptedRecoveryPackage,
  context: V2KeyContext,
  passphrase: string
): Promise<V2Keys> => {
  if (
    value.version !== 2 ||
    value.kdf !== "PBKDF2-SHA256" ||
    value.iterations !== 600_000 ||
    value.contextHash !== (await recoveryContextHash(context))
  ) {
    throw new Error("Recovery package does not match this Redacted deployment");
  }
  const key = await passphraseKey(
    passphrase,
    base64Decode(value.salt),
    value.iterations
  );
  let rootSeed: Uint8Array;
  try {
    rootSeed = new Uint8Array(
      await crypto.subtle.decrypt(
        {
          name: "AES-GCM",
          iv: base64Decode(value.nonce),
          additionalData: utf8(value.contextHash),
        },
        key,
        base64Decode(value.ciphertext)
      )
    );
  } catch {
    // WebCrypto may reject with an empty OperationError message. Neither a
    // corrupted file nor an incorrect passphrase should look like a no-op.
    throw new Error(
      "Could not decrypt this recovery backup. Check the file and passphrase, then try again."
    );
  }
  try {
    return await deriveKeysFromSeed(rootSeed, context);
  } finally {
    // The returned key set owns an independent copy. Remove the transient
    // plaintext produced by AES-GCM as soon as derivation completes.
    rootSeed.fill(0);
  }
};

export const contextForRecoveryPackage = (
  value: EncryptedRecoveryPackage,
  deployment: Pick<V2KeyContext, "chainId" | "proxyAddress">
): V2KeyContext => {
  if (
    typeof value.walletAddress !== "string" ||
    value.walletAddress.length < 10 ||
    value.walletAddress.length > 128
  ) {
    throw new Error("Recovery package has an invalid derivation wallet");
  }
  return { ...deployment, walletAddress: value.walletAddress };
};

export const keyFingerprint = async (keys: V2Keys): Promise<string> =>
  base64Encode(
    (await sha256(concatBytes(keys.scanKey, utf8(keys.ownerKey)))).slice(0, 12)
  );
